// screens/ProfScreen.tsx — H2: open-ended chat with Il Prof.
import type { JSX } from 'react';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Screen } from '../components/Screen';
import { TopChrome } from '../components/TopChrome';
import { CronologiaPanel } from '../components/CronologiaPanel';
import { ConfirmModal } from '../components/ConfirmModal';
import { sessionsApi, type RecentSession } from '../api/sessions';
import { resumeProfSession } from '../state/prof-resume';
import { ChatThread, type ProfFeedbackSignal } from '../components/ChatThread';
import { ActionChips } from '../components/ActionChips';
import { Composer } from '../components/Composer';
import { ProfFigureContext } from '../components/ProfFigureContext';
import { getToken } from '../api/client';
import { prepareProfFigureTurn, profFigureSessionId, receiveProfFigureEvent, receiveProfFigureError, selectProfFigure } from '../state/prof-figure';
import { parseProfFigureReference, type ProfFigureReference } from '../types/prof-figure';
import { ProfMessage } from '../components/ProfMessage';
import VoiceCallScreen, { type VoiceCallStatus } from './VoiceCallScreen';
import { ProfMark } from '../components/ProfAvatar';
import { useChat } from '../state/chat';
import { useTokens } from '../state/theme';
import { useSpeechSynthesis } from '../utils/useSpeechSynthesis';
import { useQwenRealtimeVoice, type QwenServerVisualSetPayload } from '../utils/useQwenRealtimeVoice';
import { buildVoiceContextFromProfState } from '../utils/voice-context';
import {
  buildSpeedLimitVisualTeachingProse,
} from '../utils/qwen-voice-display';
import {
  detectQwenVoiceVisualTopicFromText,
  getQwenVoiceVisualTopicForImageId,
} from '../utils/qwen-voice-visual-registry';
import {
  resolveVoiceVisualSet,
  visualSetToVisibleImages,
  type VoiceVisualShownIdsByTopic,
  type VoiceVisualSet,
} from '../utils/qwen-voice-visual-set';
import { recordQwenVoiceTrace } from '../utils/qwen-voice-trace';
import { useFeatureFlag } from '../utils/useFeatureFlag';
import { persistQwenRealtimeVoiceTurn, type QwenRealtimeUsage, type VoiceContextV1 } from '../api/qwen-realtime';
import { chiediStream } from '../api/tutor';
import { profFeedbackApi } from '../api/prof-feedback';
import {
  profCheckpointApi,
  type ProfCheckpointEncounter,
  type ProfCheckpointResult,
} from '../api/prof-checkpoint';
import { ProfCheckpointCard } from '../components/ProfCheckpointCard';
import type { ChiediHistoryMessage } from '../api/tutor';
import type { Message } from '../types/domain';
import { getStoredFirstName } from '../App';
import { AI_DISCLOSURE_ACK_EVENT, hasSeenAIDisclosure } from '../components/AIActDisclosureModal';

const THREAD_ID = 'chiedi';
const EMPTY_MESSAGES: Message[] = [];

// OA1 — per-message checkpoint card state (mirrors ProfChatPanel's shape).
type ProfCheckpointUiState =
  | { kind: 'pending' }
  | { kind: 'error' }
  | {
      kind: 'ready';
      encounter: ProfCheckpointEncounter;
      idempotencyKey: string;
      sessionId: string;
    }
  | {
      kind: 'graded';
      result: ProfCheckpointResult;
      questionText: string | null;
    };
const QWEN_REALTIME_VOICE_FLAG = 'qwen_realtime_voice_enabled';
// FP7 §1 — one-shot first-use hint flag for the hold-to-talk voice note.
const VOICE_NOTE_HINT_KEY = 'asc_voice_note_hint_v1';
type ProfRouteState = {
  prefill?: string;
  autoSend?: boolean;
  figureReference?: ProfFigureReference;
  figurePreview?: { imageRef: string; label: string };
  // Cross-surface voice entry (GlobalChatSheet → /prof): start a voice CALL on
  // arrival. The sheet has no local voice engine, so it routes to /prof and asks
  // ProfScreen (which owns the qwen hook) to begin the call. One-shot consumed.
  startCall?: boolean;
  topicHint?: {
    argomentoId: string;
    argomentoName?: string;
    evidence?: {
      principlesObserved: number;
      principlesTotal: number;
      principlesMastered: number;
      readinessPct: number;
      evidenceLabel: 'Non ancora osservato' | 'Pochi dati' | 'Da rinforzare' | 'In consolidamento' | 'Buona base';
      weaknessReason: 'observed_errors' | 'low_coverage' | 'mixed' | 'none';
    };
  };
};

type VoicePersistStatus = 'idle' | 'saving' | 'saved' | 'duplicate' | 'error';

declare global {
  interface Window {
    __autoscuolaSubmitQwenVoiceText?: (text: string) => void;
  }
}

interface PendingQwenVoiceTurn {
  turnClientId: string;
  userText: string;
  contextText: string;
  assistantText: string;
  providerResponseId?: string;
  providerUsage?: QwenRealtimeUsage;
  providerUsageHash?: string;
  turnProof?: string;
  localRepair?: boolean;
  // Voice-visuals rebuild P0a — this turn changed/clarified the single current
  // visual set (no marker bubble was appended; the overlay slot owns the images).
  // Used by the done/guard handlers to apply the empty-answer fallback + relax
  // the persistence proof requirement for a visual-only turn, exactly as the old
  // marker-bubble turn did, without an injected message id.
  visualSetTurn?: boolean;
  skipProviderPersistence?: boolean;
}

function latestCompletedProfText(messages: Message[]): { id: string; text: string } | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.authorKind === 'prof' && message.kind === 'text' && 'text' in message) {
      const text = message.text.trim();
      if (text) return { id: message.id, text };
    }
  }
  return null;
}

// FP7 §1 (Mode B) — the spoken fallback when the model didn't fill CTRL `spoken`:
// the first paragraph of the answer (markdown markers stripped, capped). Keeps the
// spoken rendition short without reading the whole answer aloud.
function firstParagraph(text: string, maxChars = 300): string {
  const firstBlock = text.split(/\n\s*\n/)[0] ?? text;
  const clean = firstBlock
    .replace(/\[\[image:[^\]]+\]\]/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/^#+\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
  return clean.length <= maxChars ? clean : clean.slice(0, maxChars).replace(/\s+\S*$/, '');
}

function recentTextContext(messages: Message[], maxMessages = 6): string {
  return messages
    .filter((m) => (m.authorKind === 'you' || m.authorKind === 'prof') && m.kind === 'text' && 'text' in m)
    .slice(-maxMessages)
    .map((m) => (m as { text: string }).text)
    .join('\n');
}

const QWEN_APP_BOUNDARY_TEXT =
  "Mi dispiace, sembra un problema dell'applicazione: io posso aiutarti solo con regole, segnali, quiz e preparazione Patente B. Segnalalo al tuo istruttore o alla scuola.";
const QWEN_PERSONA_DRIFT_FALLBACK_TEXT =
  "Riprendiamo il punto di prima: dimmi quale passaggio vuoi rivedere e lo colleghiamo subito a una regola Patente B.";

function normalizeVoiceComplaintText(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isQwenVoiceAppComplaint(text: string): boolean {
  const normalized = normalizeVoiceComplaintText(text);
  if (/\b(?:app|applicazione|bug|debug|qwen|screenshot|websocket)\b/.test(normalized)) return true;
  const mentionsAppSurface = /\b(?:caricamento|chiamata|microfono|rete|voce)\b/.test(normalized) ||
    /\bconversazione\b/.test(normalized);
  const mentionsFailure = /\b(?:bloccata|bloccato|cambiata|cambiato|chiusa|chiuso|errore|interrotta|interrotto|non funziona|non mi ascolta|non parte|non riesco|problema)\b/.test(normalized);
  return (mentionsAppSurface && mentionsFailure) ||
    /(?:immagini|segnali|cartelli).{0,32}(?:non si sono viste|non funzionano|non appaiono|non si vedono|non me li hai mostrati|non mi hai mostrato|non hai mostrato)/.test(normalized) ||
    /(?:non si sono viste|non funzionano|non appaiono|non si vedono|non me li hai mostrati|non mi hai mostrato|non hai mostrato).{0,32}(?:immagini|segnali|cartelli)/.test(normalized) ||
    /(?:immagini|segnali).{0,24}(?:non si sono viste|non funzionano|non appaiono|non si vedono)/.test(normalized) ||
    /(?:non si sono viste|non funzionano|non appaiono|non si vedono).{0,24}(?:immagini|segnali)/.test(normalized);
}

function isQwenLearningContinuationRequest(text: string): boolean {
  const normalized = normalizeVoiceComplaintText(text);
  return /\b(?:non ho capito|non mi e chiaro|mi sono perso|mi sono persa|ripeti|ripetilo|riprendiamo|riprendere|riprendi|spiegamelo meglio|spiegami meglio|torniamo al punto|punto di prima)\b/.test(normalized);
}

function isLikelyTopicSwitchAwayFromActiveVisuals(
  text: string,
  activeImages: VoiceContextV1['currentVisibleImages'] | undefined,
): boolean {
  if (!activeImages?.length) return false;
  const normalized = normalizeVoiceComplaintText(text);
  const activeTopics = new Set(
    activeImages
      .map((image) => getQwenVoiceVisualTopicForImageId(`${image.id} ${image.alt ?? ''}`))
      .filter((topicId) => topicId !== 'unknown'),
  );
  if (activeTopics.size === 0) return false;
  const explicitTopic = detectQwenVoiceVisualTopicFromText(normalized).topicId;
  if (explicitTopic !== 'unknown') return !activeTopics.has(explicitTopic);

  const deicticVisualReference = /\b(?:cartelli?|segnali?|immagini?|questi|queste|quelli|quelle|vedere|mostrare|mostrarli|mostrameli|parlarne|parlamene|parlarmene)\b/.test(normalized);
  if (deicticVisualReference) return false;

  return /\b(?:aderenza|asciutto|bagnato|frenata|frenare|nebbia|pioggia|rotatori[ae]|semaforo)\b/.test(normalized);
}

function withoutStaleVisualContext(context: VoiceContextV1): VoiceContextV1 {
  const { currentVisibleImages: _currentVisibleImages, visiblePrompt, ...rest } = context;
  if (visiblePrompt?.kind === 'card' && /^Domanda dello studente rimasta senza risposta:/i.test(visiblePrompt.text)) {
    return rest;
  }
  return visiblePrompt ? { ...rest, visiblePrompt } : rest;
}

function isExplicitCorrectionToEarlierTask(text: string): boolean {
  const normalized = normalizeVoiceComplaintText(text);
  return /\b(?:mia risposta|il mio non lo so|quello che ho detto|quello che intendevo)\b.{0,80}\b(?:relativa|relativo|riferita|riferito|riguardava|era per)\b/.test(normalized) ||
    /\b(?:mi riferivo|intendevo|stavo rispondendo|volevo rispondere)\b.{0,80}\b(?:domanda|punto|asfalto|bagnato|precedenza|limiti|sorpasso|sosta|segnale)\b/.test(normalized) ||
    /\b(?:non stavo rispondendo|non era riferito|non era riferita)\b/.test(normalized);
}

function withStudentCorrectionContext(context: VoiceContextV1, text: string): VoiceContextV1 {
  const base = withoutStaleVisualContext(context);
  const { currentVisibleImages: _currentVisibleImages, ...rest } = base;
  return {
    ...rest,
    visiblePrompt: {
      kind: 'card',
      text: `Correzione dello studente: ${text.trim().slice(0, 700)}`,
    },
  };
}

// V28 S1 — exactly 4 conversational quick chips (prototype prof.html §51-54).
// Each one STARTS A CONVERSATION via handleSend; NONE navigates to a section.
const START_CHIPS: ReadonlyArray<{ label: string; prompt: string }> = [
  { label: 'Spiegami una regola', prompt: 'Spiegami una regola del Codice della Strada.' },
  { label: 'Facciamo qualche domanda', prompt: 'Facciamo qualche domanda di esame insieme.' },
  { label: 'Cosa devo ripassare?', prompt: 'Cosa mi conviene ripassare adesso?' },
  { label: 'Ho un dubbio', prompt: 'Ho un dubbio, posso chiederti una cosa?' },
];

// B9 (V29 S2): the per-turn persist status ("Salvata / Non salvata / Già
// salvata") is INTERNAL save-state — it no longer renders in the call overlay.
// The voicePersistStatus machinery stays: it drives the persistence retry path.

export default function ProfScreen(): JSX.Element {
  const location = useLocation();
  const tokens = useTokens();
  const preparingFigureTurn = useRef(false);
  const savingFigure = useChat((s) => s.pendingProfFiguresByThread[THREAD_ID]?.status === 'saving');
  const [cronologiaOpen, setCronologiaOpen] = useState(false);
  const [confirmNewConversation, setConfirmNewConversation] = useState(false);
  // V29 S7 (F15) — the inline "Riprendi da dove eri" card: latest resumable
  // chiedi session, fetched once per empty-state mount.
  const [resumeCandidate, setResumeCandidate] = useState<RecentSession | null>(null);
  const [resuming, setResuming] = useState(false);
  // FP3a: follow-up / continuation chips for the last assistant turn. Now held
  // in the persisted chat store (keyed to the Chiedi thread) instead of local
  // React state, so a refresh / resume of the same thread restores them
  // (audit finding — chips previously vanished on refresh). `setLastSuggestions`
  // is a thin wrapper over the store action so the existing call sites below
  // (new send, refusal, drawer reset, "Nuova conversazione") work unchanged.
  const lastSuggestions = useChat((s) => s.lastSuggestions);
  const setLastSuggestions = useCallback(
    (suggestions: string[] | null) => useChat.getState().setLastSuggestions(suggestions),
    [],
  );
  // If the last exchange errored (network drop etc.), stash the question that
  // failed so we can offer a one-tap "Riprova" chip to re-send it.
  const [retryQuestion, setRetryQuestion] = useState<string | null>(null);
  const [voiceMode, setVoiceMode] = useState(false);
  // Value intentionally unread since B9 removed the overlay label; the setter
  // keeps the save-health transitions in one place for a future failure toast
  // (S8 decides retention vs removal).
  const [, setVoicePersistStatus] = useState<VoicePersistStatus>('idle');
  // D2 — realtime-call failure state: switches the session to the read-aloud
  // fallback and drives the on-screen disclosure notice (never silent).
  const [qwenCallFailed, setQwenCallFailed] = useState(false);
  const [voiceFallbackNoticeDismissed, setVoiceFallbackNoticeDismissed] = useState(false);
  // FP7 §1 — first-use hint for the layered hold-to-talk affordance. Shown once,
  // dismissed on first voice note or explicit close; persisted in localStorage so
  // it never nags after the student has discovered it.
  const [showVoiceNoteHint, setShowVoiceNoteHint] = useState(
    () => {
      try { return localStorage.getItem(VOICE_NOTE_HINT_KEY) !== '1'; } catch { return false; }
    },
  );
  const dismissVoiceNoteHint = useCallback(() => {
    setShowVoiceNoteHint(false);
    try { localStorage.setItem(VOICE_NOTE_HINT_KEY, '1'); } catch { /* private mode — fine */ }
  }, []);
  const [profFeedbackByMessageId, setProfFeedbackByMessageId] = useState<Record<string, ProfFeedbackSignal>>({});
  const profFeedbackQueuesRef = useRef<Record<string, Promise<void>>>({});
  const profFeedbackRevisionsRef = useRef<Record<string, number>>({});
  // OA1 — checkpoint cards on the main /prof surface (previously only the
  // GlobalChatSheet's ProfChatPanel had this wiring; the socio used /prof).
  const [checkpointUiByMessageId, setCheckpointUiByMessageId] =
    useState<Record<string, ProfCheckpointUiState>>({});
  // One-shot consumption guard for the router prefill (see useEffect below).
  const consumedPrefillRef = useRef(false);
  const spokenMessageIdRef = useRef<string | null>(null);
  const qwenAssistantStreamingRef = useRef(false);
  const qwenPendingTurnRef = useRef<PendingQwenVoiceTurn | null>(null);
  const qwenPersistenceAttemptedRef = useRef<Set<string>>(new Set());
  const qwenSuppressProviderCallbacksRef = useRef(false);
  const qwenStartRequestedRef = useRef(false);
  const wasQwenSupportedRef = useRef(false);
  const consumedStartCallRef = useRef(false);
  // MM:SS call timer for the VoiceCallScreen overlay (was owned by VoiceModeDock).
  const [callSeconds, setCallSeconds] = useState(0);
  // Voice-visuals rebuild P0a — the SINGLE current visual set. It is REPLACED (not
  // appended) on each new selection and rendered in ONE dedicated slot in the
  // VoiceCallScreen overlay; the SAME set feeds the model's currentVisibleImages,
  // so what is on screen == what the model is told, by construction. Held in both
  // state (the render slot reacts) and a ref (sync read inside the transcript
  // handler). Per-turn renderStatus tells the model the ground truth (shown_new |
  // unchanged | none) — the visual-state echo that kills the apology loop (RC4).
  const [activeVisualSet, setActiveVisualSet] = useState<VoiceVisualSet | null>(null);
  const activeVisualSetRef = useRef<VoiceVisualSet | null>(null);
  const activeVisualShownIdsByTopicRef = useRef<VoiceVisualShownIdsByTopic>({});
  const lastRenderStatusRef = useRef<VoiceContextV1['renderStatus']>(undefined);
  const setVoiceVisualSet = useCallback((next: VoiceVisualSet | null) => {
    activeVisualSetRef.current = next;
    if (!next) {
      activeVisualShownIdsByTopicRef.current = {};
    }
    setActiveVisualSet(next);
  }, []);
  const {
    supported: speechSupported,
    speaking,
    speak,
    stop: stopSpeech,
  } = useSpeechSynthesis();

  // Voice-visuals rebuild P0a/P1a — derive the model's belief about the screen
  // from the SINGLE current visual set, not from the latest prof marker message.
  // The set is the source of truth: its signs become currentVisibleImages and the
  // per-turn renderStatus (shown_new | unchanged | none) is the visual echo.
  const buildCurrentVoiceContext = useCallback((renderStatusOverride?: VoiceContextV1['renderStatus']) => {
    const context = buildVoiceContextFromProfState({
      messages: useChat.getState().threads[THREAD_ID]?.messages ?? EMPTY_MESSAGES,
      pathname: location.pathname,
      routeState: location.state as ProfRouteState | null,
    });
    const setImages = visualSetToVisibleImages(activeVisualSetRef.current);
    // The current visual set OWNS currentVisibleImages — replace whatever the
    // message-derived path produced (it stays only as the cold-start question
    // imageRef path when no set is active).
    if (setImages?.length) {
      context.currentVisibleImages = setImages;
    } else if (!activeVisualSetRef.current) {
      // No set active: keep the question-imageRef path (V/F card on screen) but
      // drop any stale marker-derived sign images so we never over-claim.
      if (context.currentVisibleImages?.some((img) => img.kind === 'sign')) {
        delete context.currentVisibleImages;
      }
    }
    const renderStatus = renderStatusOverride ?? lastRenderStatusRef.current;
    if (renderStatus) context.renderStatus = renderStatus;
    return context;
  }, [location.pathname, location.state]);

  const handleQwenUserTranscript = useCallback((text: string) => {
    const clean = text.trim();
    if (!clean) return undefined;
    qwenSuppressProviderCallbacksRef.current = false;
    const contextText = recentTextContext(useChat.getState().threads[THREAD_ID]?.messages ?? EMPTY_MESSAGES);
    if (qwenAssistantStreamingRef.current) {
      useChat.getState().removeStreamingPlaceholder(THREAD_ID);
      useChat.getState().setStreaming(THREAD_ID, false);
      qwenAssistantStreamingRef.current = false;
    }
    setVoicePersistStatus('idle');
    useChat.getState().appendMessage(THREAD_ID, {
      id: crypto.randomUUID(),
      authorKind: 'you',
      kind: 'text',
      text: clean,
    });

    const turnClientId = `qwen-${Date.now()}-${crypto.randomUUID()}`;
    if (isQwenVoiceAppComplaint(clean)) {
      useChat.getState().appendMessage(THREAD_ID, {
        id: crypto.randomUUID(),
        authorKind: 'prof',
        kind: 'text',
        text: QWEN_APP_BOUNDARY_TEXT,
      });
      qwenPendingTurnRef.current = null;
      qwenSuppressProviderCallbacksRef.current = true;
      recordQwenVoiceTrace({
        source: 'chiedi-screen',
        type: 'visible_message_final',
        turnId: turnClientId,
        textSnippet: QWEN_APP_BOUNDARY_TEXT,
        details: { phase: 'app_complaint_boundary_local' },
      });
      return {
        handledLocally: true as const,
        context: buildCurrentVoiceContext(),
      };
    }

    // ── Voice-visuals rebuild P0/P1/P2 — the SINGLE visual-set resolver ──
    // One tested function (resolveVoiceVisualSet) decides show/replace/reuse/keep/
    // clarify, sourced from the shared registry with a CONFIDENCE FLOOR. It does
    // NOT append a marker bubble: the dedicated overlay slot owns the images.
    const decision = resolveVoiceVisualSet({
      userText: clean,
      contextText,
      currentSet: activeVisualSetRef.current,
      shownIdsByTopic: activeVisualShownIdsByTopicRef.current,
    });
    recordQwenVoiceTrace({
      source: 'chiedi-screen',
      type: decision.action === 'replace' || decision.action === 'reuse'
        ? 'visible_message_final'
        : 'context_update_queued',
      turnId: turnClientId,
      textSnippet: clean,
      details: {
        phase: `visual_set_${decision.action}`,
        topicId: decision.topicId,
        confidence: decision.confidence,
        renderStatus: decision.renderStatus,
      },
    });

    qwenPendingTurnRef.current = {
      turnClientId,
      userText: clean,
      contextText,
      assistantText: '',
      visualSetTurn: decision.action === 'replace' || decision.action === 'reuse' || decision.action === 'clarify',
    };

    if (decision.action === 'replace' || decision.action === 'reuse') {
      // REPLACE (not append) the single current set. The slot re-renders; the
      // model's currentVisibleImages + renderStatus come from this same set.
      setVoiceVisualSet(decision.nextSet);
      if (decision.action === 'replace' && decision.historyUpdate) {
        activeVisualShownIdsByTopicRef.current = {
          [decision.historyUpdate.topicId]: decision.historyUpdate.shownIds,
        };
      }
      lastRenderStatusRef.current = decision.renderStatus;
      const context = buildCurrentVoiceContext(decision.renderStatus);
      return {
        context,
        responseGuard: {
          kind: 'visual-aid' as const,
          context,
          failureMessage:
            'Guardiamo insieme il segnale qui sopra: dimmi pure cosa vuoi capire.',
        },
      };
    }

    if (decision.action === 'clarify') {
      // Fail-closed: a visual ask we cannot confidently route. Keep whatever is
      // shown, tell the model the ground truth, and ask a clarifying question — no
      // stale or guessed sign claimed.
      lastRenderStatusRef.current = decision.renderStatus;
      const context = buildCurrentVoiceContext(decision.renderStatus);
      return {
        context,
        responseGuard: {
          kind: 'visible-images' as const,
          context,
          failureMessage:
            decision.clarifyMessage ??
            'Dimmi di quale segnale parliamo e te lo mostro subito.',
        },
      };
    }

    // decision.action === 'keep' — a non-visual turn. Fall through to the
    // correction / topic-switch / persona-boundary / plain paths below, but FIRST
    // decide whether the non-visual turn moved AWAY from the shown topic so the
    // set + its echo must be cleared (no stale visual claim).
    const movedAway = isLikelyTopicSwitchAwayFromActiveVisuals(
      clean,
      visualSetToVisibleImages(activeVisualSetRef.current),
    );
    if (movedAway && activeVisualSetRef.current) {
      setVoiceVisualSet(null);
      lastRenderStatusRef.current = 'none';
      recordQwenVoiceTrace({
        source: 'chiedi-screen',
        type: 'context_update_queued',
        turnId: turnClientId,
        textSnippet: clean,
        details: { phase: 'visual_set_cleared_for_topic_switch' },
      });
    } else {
      lastRenderStatusRef.current = decision.renderStatus;
    }

    const rawContext = buildCurrentVoiceContext(lastRenderStatusRef.current);
    if (isExplicitCorrectionToEarlierTask(clean)) {
      setVoiceVisualSet(null);
      lastRenderStatusRef.current = 'none';
      const context = withStudentCorrectionContext(
        buildCurrentVoiceContext('none'),
        clean,
      );
      recordQwenVoiceTrace({
        source: 'chiedi-screen',
        type: 'context_update_queued',
        turnId: turnClientId,
        textSnippet: clean,
        details: { phase: 'student_correction_current_task' },
      });
      return { context };
    }

    const context = movedAway ? withoutStaleVisualContext(rawContext) : rawContext;
    if (context.currentVisibleImages?.length) {
      return {
        context,
        responseGuard: {
          kind: 'visible-images' as const,
          context,
          failureMessage:
            'Guardiamo insieme questo segnale: quale dettaglio vuoi che ti spieghi?',
        },
      };
    }
    if (isQwenLearningContinuationRequest(clean)) {
      return {
        context,
        responseGuard: {
          kind: 'persona-boundary' as const,
          context,
          failureMessage: QWEN_PERSONA_DRIFT_FALLBACK_TEXT,
        },
      };
    }
    return { context };
  }, [buildCurrentVoiceContext, setVoiceVisualSet]);

  // Voice unification §4.3 — mirror the SERVER's per-turn visual decision. The
  // proxy already grounded the model on this exact decision (its instructions
  // update precedes the released response.create), so the local resolver above
  // is only the provisional render; the server decision WINS when it lands.
  const lastServerVisualTxRef = useRef<string | null>(null);
  const handleQwenServerVisualSet = useCallback((payload: QwenServerVisualSetPayload): VoiceContextV1 | void => {
    if (payload.transactionId === lastServerVisualTxRef.current) return undefined;
    lastServerVisualTxRef.current = payload.transactionId;
    if (payload.action === 'keep') return undefined;
    if (payload.action === 'clear_clarify') {
      if (!activeVisualSetRef.current && lastRenderStatusRef.current === 'none') return undefined;
      setVoiceVisualSet(null);
      lastRenderStatusRef.current = 'none';
      return buildCurrentVoiceContext('none');
    }
    // replace — same-set dedupe kills flicker when local already showed it.
    const nextSigns = payload.signs
      .filter((sign) => sign && typeof sign.name === 'string' && sign.name.trim().length > 0)
      .slice(0, 3)
      .map((sign) => ({ kind: 'sign' as const, id: sign.name, alt: sign.label || sign.name }));
    if (nextSigns.length === 0) return undefined;
    const currentIds = (activeVisualSetRef.current?.signs ?? []).map((s) => s.id).join('|');
    const nextIds = nextSigns.map((s) => s.id).join('|');
    if (currentIds === nextIds) {
      if (lastRenderStatusRef.current !== 'unchanged') {
        lastRenderStatusRef.current = 'unchanged';
        return buildCurrentVoiceContext('unchanged');
      }
      return undefined;
    }
    setVoiceVisualSet({
      topicId: 'unknown',
      signs: nextSigns,
      transactionId: payload.transactionId,
    });
    lastRenderStatusRef.current = 'shown_new';
    return buildCurrentVoiceContext('shown_new');
  }, [buildCurrentVoiceContext, setVoiceVisualSet]);

  const handleQwenAssistantDelta = useCallback((delta: string) => {
    if (!delta) return;
    if (qwenSuppressProviderCallbacksRef.current) return;
    const pending = qwenPendingTurnRef.current;
    if (pending?.localRepair) return;
    if (pending) {
      pending.assistantText = `${pending.assistantText}${delta}`;
    }
    if (!qwenAssistantStreamingRef.current) {
      qwenAssistantStreamingRef.current = true;
      useChat.getState().appendMessage(THREAD_ID, {
        id: crypto.randomUUID(),
        authorKind: 'prof',
        kind: 'streaming',
        text: '',
      });
      useChat.getState().setStreaming(THREAD_ID, true);
    }
    useChat.getState().appendToLastMessage(THREAD_ID, delta);
  }, []);

  const persistCompletedQwenTurn = useCallback(async (turn: PendingQwenVoiceTurn) => {
    const sessionId = useChat.getState().chiediSessionId;
    if (!sessionId || qwenPersistenceAttemptedRef.current.has(turn.turnClientId)) return;
    qwenPersistenceAttemptedRef.current.add(turn.turnClientId);
    setVoicePersistStatus('saving');
    try {
      const result = await persistQwenRealtimeVoiceTurn({
        sessionId,
        turnClientId: turn.turnClientId,
        userText: turn.userText,
        assistantText: turn.assistantText,
        turnProof: turn.turnProof ?? '',
        ...(turn.providerResponseId ? { providerResponseId: turn.providerResponseId } : {}),
        ...(turn.providerUsage ? { providerUsage: turn.providerUsage } : {}),
        ...(turn.providerUsageHash ? { providerUsageHash: turn.providerUsageHash } : {}),
      });
      setVoicePersistStatus(result.duplicate ? 'duplicate' : 'saved');
    } catch {
      setVoicePersistStatus('error');
    }
  }, []);

  const handleQwenAssistantFinal = useCallback((text: string) => {
    const clean = text.trim();
    if (!clean) return;
    if (qwenSuppressProviderCallbacksRef.current) return;
    const pending = qwenPendingTurnRef.current;
    if (pending?.localRepair) return;
    // Founder decision D (2026-06-03): visual turns no longer suppress + combine.
    // The grounded Prof's own words stream and finalize as a normal message below
    // the shown signs — no canned text, no answer replacement.
    if (pending) {
      pending.assistantText = clean;
    }
    if (qwenAssistantStreamingRef.current) {
      useChat.getState().replaceLastMessageText(THREAD_ID, clean);
      recordQwenVoiceTrace({
        source: 'chiedi-screen',
        type: 'visible_message_final',
        turnId: pending?.turnClientId,
        textSnippet: clean,
        details: { phase: 'assistant_final_replace_streaming' },
      });
      return;
    }
    useChat.getState().appendMessage(THREAD_ID, {
      id: crypto.randomUUID(),
      authorKind: 'prof',
      kind: 'text',
      text: clean,
    });
    recordQwenVoiceTrace({
      source: 'chiedi-screen',
      type: 'visible_message_final',
      turnId: pending?.turnClientId,
      textSnippet: clean,
      details: { phase: 'assistant_final_append' },
    });
  }, []);

  const handleQwenAssistantDone = useCallback((info: {
    providerResponseId?: string;
    turnClientId?: string;
    providerUsage?: QwenRealtimeUsage;
    providerUsageHash?: string;
    turnProof?: string;
    cancelled: boolean;
  }) => {
    if (qwenSuppressProviderCallbacksRef.current) {
      qwenSuppressProviderCallbacksRef.current = false;
      if (qwenAssistantStreamingRef.current) {
        useChat.getState().removeStreamingPlaceholder(THREAD_ID);
        useChat.getState().setStreaming(THREAD_ID, false);
        qwenAssistantStreamingRef.current = false;
      }
      setVoicePersistStatus('idle');
      return;
    }
    if (info.cancelled) {
      if (qwenAssistantStreamingRef.current) {
        useChat.getState().removeStreamingPlaceholder(THREAD_ID);
        useChat.getState().setStreaming(THREAD_ID, false);
        qwenAssistantStreamingRef.current = false;
      }
      qwenPendingTurnRef.current = null;
      setVoicePersistStatus('idle');
      return;
    }
    if (qwenAssistantStreamingRef.current) {
      useChat.getState().finalizeStreamingMessage(THREAD_ID);
      useChat.getState().setStreaming(THREAD_ID, false);
      qwenAssistantStreamingRef.current = false;
    }
    const pending = qwenPendingTurnRef.current;
    if (!pending) return;
    qwenPendingTurnRef.current = null;
    if (info.turnClientId) pending.turnClientId = info.turnClientId;
    if (info.providerResponseId) pending.providerResponseId = info.providerResponseId;
    if (info.providerUsage) pending.providerUsage = info.providerUsage;
    if (info.providerUsageHash) pending.providerUsageHash = info.providerUsageHash;
    if (info.turnProof) pending.turnProof = info.turnProof;
    const isVisualTurn = Boolean(pending.visualSetTurn);
    if (isVisualTurn && !pending.assistantText.trim()) {
      // Founder decision D: the grounded Prof produced no words for the shown
      // signs (rare). Append a neutral, fact-free engagement line below the images
      // — never canned teaching facts, and never a replacement of a real answer.
      const fallback = buildSpeedLimitVisualTeachingProse();
      pending.assistantText = fallback;
      pending.skipProviderPersistence = true;
      useChat.getState().appendMessage(THREAD_ID, {
        id: crypto.randomUUID(),
        authorKind: 'prof',
        kind: 'text',
        text: fallback,
      });
      recordQwenVoiceTrace({
        source: 'chiedi-screen',
        type: 'visible_message_final',
        turnId: pending.turnClientId,
        textSnippet: fallback,
        details: { phase: 'assistant_done_visual_empty_fallback' },
      });
    }
    if (pending.skipProviderPersistence) {
      setVoicePersistStatus('idle');
      return;
    }
    if (!pending.assistantText.trim() && !isVisualTurn) {
      const last = useChat.getState().threads[THREAD_ID]?.messages.at(-1);
      if (last?.authorKind === 'prof' && 'text' in last) {
        pending.assistantText = last.text.trim();
      }
    }
    if (!pending.userText.trim() || !pending.assistantText.trim()) return;
    if (!pending.turnProof) {
      if (isVisualTurn) {
        setVoicePersistStatus('idle');
        return;
      }
      setVoicePersistStatus('error');
      return;
    }
    void persistCompletedQwenTurn(pending);
  }, [persistCompletedQwenTurn]);

  const cancelQwenUiTurn = useCallback(() => {
    if (qwenAssistantStreamingRef.current) {
      useChat.getState().removeStreamingPlaceholder(THREAD_ID);
      useChat.getState().setStreaming(THREAD_ID, false);
      qwenAssistantStreamingRef.current = false;
    }
    qwenPendingTurnRef.current = null;
    setVoicePersistStatus('idle');
  }, []);

  const handleQwenAssistantError = useCallback(() => {
    cancelQwenUiTurn();
  }, [cancelQwenUiTurn]);

  const handleQwenAssistantGuardFailed = useCallback((message: string) => {
    const pending = qwenPendingTurnRef.current;
    if (qwenAssistantStreamingRef.current) {
      useChat.getState().removeStreamingPlaceholder(THREAD_ID);
      useChat.getState().setStreaming(THREAD_ID, false);
      qwenAssistantStreamingRef.current = false;
    }
    // Voice-visuals rebuild P0a — the visual set is already rendered in the
    // overlay slot (no injected marker bubble to amend). The neutral guard
    // message is just appended as a normal Prof line below it.
    useChat.getState().appendMessage(THREAD_ID, {
      id: crypto.randomUUID(),
      authorKind: 'prof',
      kind: 'text',
      text: message,
    });
    recordQwenVoiceTrace({
      source: 'chiedi-screen',
      type: 'visible_message_final',
      turnId: pending?.turnClientId,
      textSnippet: message,
      details: { phase: pending?.visualSetTurn ? 'assistant_guard_failed_visual' : 'assistant_guard_failed_append' },
    });
    qwenPendingTurnRef.current = null;
    qwenSuppressProviderCallbacksRef.current = true;
    useChat.getState().setStreaming(THREAD_ID, false);
    setVoicePersistStatus('idle');
  }, []);

  const qwenRealtimeVoiceEnabled = useFeatureFlag(QWEN_REALTIME_VOICE_FLAG);
  // OA4 — the FE-read flag for the dictation-review gesture (D4 option B) and
  // the durable in-bubble recovery actions. Default off, fail-closed: with the
  // flag off the FP7 auto-send + below-thread chips stay byte-identical.
  const oa4ReviewEnabled = useFeatureFlag('oa4_dictation_review_enabled');
  const chiediSessionId = useChat((s) => s.chiediSessionId);
  const messages = useChat((s) => s.threads[THREAD_ID]?.messages ?? EMPTY_MESSAGES);
  const isStreaming = useChat((s) => s.threads[THREAD_ID]?.streaming ?? false);
  // B4 (V29 S2): store-backed citation expansion — same wiring as ProfChatPanel.
  const expandedPrinciple = useChat((s) => s.expandedPrinciple);
  const expandPrinciple = useChat((s) => s.expandPrinciple);
  const collapsePrinciple = useChat((s) => s.collapsePrinciple);

  useEffect(() => {
    if (!chiediSessionId) {
      setProfFeedbackByMessageId({});
      return;
    }
    let alive = true;
    profFeedbackApi.list(chiediSessionId)
      .then(({ feedback }) => {
        if (!alive) return;
        setProfFeedbackByMessageId(Object.fromEntries(feedback.map((row) => [row.messageId, row.signal])));
      })
      .catch(() => { /* feedback availability never blocks the Prof */ });
    return () => { alive = false; };
  }, [chiediSessionId]);

  const handleProfFeedback = useCallback(async (messageId: string, feedback: ProfFeedbackSignal | null) => {
    let previous: ProfFeedbackSignal | undefined;
    setProfFeedbackByMessageId((current) => {
      previous = current[messageId];
      const next = { ...current };
      if (feedback === null) delete next[messageId];
      else next[messageId] = feedback;
      return next;
    });
    const revision = (profFeedbackRevisionsRef.current[messageId] ?? 0) + 1;
    profFeedbackRevisionsRef.current[messageId] = revision;
    const previousWrite = profFeedbackQueuesRef.current[messageId] ?? Promise.resolve();
    const write = previousWrite
      .catch(() => undefined)
      .then(async () => {
        if (feedback === null) await profFeedbackApi.remove(messageId);
        else await profFeedbackApi.upsert(messageId, feedback);
      });
    profFeedbackQueuesRef.current[messageId] = write;
    try {
      await write;
    } catch {
      if (profFeedbackRevisionsRef.current[messageId] !== revision) return;
      setProfFeedbackByMessageId((current) => {
        const next = { ...current };
        if (previous === undefined) delete next[messageId];
        else next[messageId] = previous;
        return next;
      });
    } finally {
      if (profFeedbackQueuesRef.current[messageId] === write) {
        delete profFeedbackQueuesRef.current[messageId];
      }
    }
  }, []);

  // OA1 — student-initiated re-issue (the card's onRefresh after expiry);
  // mirrors ProfChatPanel's handleRequestCheckpoint.
  const handleRequestCheckpoint = useCallback(async (messageId: string, sessionId: string) => {
    setCheckpointUiByMessageId((current) => ({
      ...current,
      [messageId]: { kind: 'pending' },
    }));
    try {
      const { encounter } = await profCheckpointApi.issue(sessionId, messageId);
      setCheckpointUiByMessageId((current) => ({
        ...current,
        [messageId]: {
          kind: 'ready',
          encounter,
          idempotencyKey: crypto.randomUUID(),
          sessionId,
        },
      }));
    } catch {
      setCheckpointUiByMessageId((current) => ({
        ...current,
        [messageId]: { kind: 'error' },
      }));
    }
  }, []);

  const checkpointByMessageId = Object.fromEntries(
    Object.entries(checkpointUiByMessageId).flatMap(([messageId, state]) => {
      if (state.kind === 'ready') {
        return [[
          messageId,
          <ProfCheckpointCard
            key={state.encounter.id}
            encounter={state.encounter}
            onRefresh={() => { void handleRequestCheckpoint(messageId, state.sessionId); }}
            onSubmit={async (answer) => {
              const { result } = await profCheckpointApi.submit(
                state.encounter.id,
                state.idempotencyKey,
                answer,
              );
              return result;
            }}
          />,
        ]];
      }
      if (state.kind === 'graded' && state.questionText) {
        return [[
          messageId,
          <ProfCheckpointCard
            key={state.result.encounterId}
            encounter={{
              id: state.result.encounterId,
              questionId: state.result.encounterId,
              questionText: state.questionText,
              expiresAt: state.result.completedAt,
            }}
            initialResult={state.result}
            onRefresh={() => undefined}
            onSubmit={() => Promise.resolve(state.result)}
          />,
        ]];
      }
      return [];
    }),
  );
  const checkpointIssueStateByMessageId = Object.fromEntries(
    Object.entries(checkpointUiByMessageId).flatMap(([messageId, state]) => (
      state.kind === 'pending' || state.kind === 'error'
        ? [[messageId, state.kind]]
        : []
    )),
  ) as Record<string, 'pending' | 'error'>;

  const voiceContext = useMemo<VoiceContextV1>(() => {
    const context = buildVoiceContextFromProfState({
      messages,
      pathname: location.pathname,
      routeState: location.state as ProfRouteState | null,
    });
    // Voice-visuals rebuild P0a — the current visual set OWNS currentVisibleImages
    // for the session-start context too (so a reconnect/mint reflects what's shown).
    const setImages = visualSetToVisibleImages(activeVisualSet);
    if (setImages?.length) {
      return {
        ...context,
        currentVisibleImages: setImages,
        ...(lastRenderStatusRef.current ? { renderStatus: lastRenderStatusRef.current } : {}),
      };
    }
    return context;
  }, [location.pathname, location.state, messages, activeVisualSet]);
  const qwenVoice = useQwenRealtimeVoice({
    sessionId: chiediSessionId,
    context: voiceContext,
    onSession: (id) => {
      useChat.getState().setChiediSessionId(id);
    },
    onUserTranscriptFinal: handleQwenUserTranscript,
    onServerVisualSet: handleQwenServerVisualSet,
    onAssistantTranscriptDelta: handleQwenAssistantDelta,
    onAssistantTranscriptFinal: handleQwenAssistantFinal,
    onAssistantDone: handleQwenAssistantDone,
    onAssistantGuardFailed: handleQwenAssistantGuardFailed,
    onAssistantError: handleQwenAssistantError,
    enabled: qwenRealtimeVoiceEnabled,
  });

  useEffect(() => {
    const qwenTraceEnabled =
      new URLSearchParams(window.location.search).has('qwenVoiceTrace') ||
      localStorage.getItem('asc_qwen_voice_trace_v1') === '1';
    if (!qwenVoice.supported || !qwenTraceEnabled) return undefined;
    window.__autoscuolaSubmitQwenVoiceText = (text: string) => {
      qwenVoice.submitText(text);
    };
    return () => {
      if (window.__autoscuolaSubmitQwenVoiceText) {
        delete window.__autoscuolaSubmitQwenVoiceText;
      }
    };
  }, [qwenVoice.supported, qwenVoice.submitText]);

  useEffect(() => {
    // Only initialize if there's no persisted thread — otherwise we'd wipe
    // the conversation on every mount.
    if (!useChat.getState().threads[THREAD_ID]) {
      useChat.getState().startThread(THREAD_ID);
    }
  }, []);

  useEffect(() => {
    if (qwenVoice.supported && !qwenCallFailed) return;
    if (!voiceMode || !speechSupported || isStreaming) return;
    const latest = latestCompletedProfText(messages);
    if (!latest || latest.id === spokenMessageIdRef.current) return;
    spokenMessageIdRef.current = latest.id;
    speak(latest.text, { lang: 'it-IT' });
  }, [qwenVoice.supported, qwenCallFailed, voiceMode, speechSupported, isStreaming, messages, speak]);

  // D2 (founder decision, verbatim requirement: "i'd prefer to have a message
  // on screen saying we are switching to the fallback so i know the system
  // failed"): a FAILED realtime call switches this session to the read-aloud
  // fallback AND SAYS SO — no silent degradation. The failed state clears when
  // a later call actually connects.
  useEffect(() => {
    if (qwenVoice.status !== 'error') return;
    if (!voiceMode && !qwenStartRequestedRef.current) return;
    qwenStartRequestedRef.current = false;
    setQwenCallFailed(true);
    setVoiceFallbackNoticeDismissed(false);
    setVoiceMode(true);
  }, [qwenVoice.status, voiceMode]);
  useEffect(() => {
    if (qwenVoice.active) setQwenCallFailed(false);
  }, [qwenVoice.active]);

  // Only explicit handoff CTAs may turn route state into a sent student turn.
  // A plain /prof landing must stay a clean chat unless the student deliberately
  // asks something.
  useEffect(() => {
    if (consumedPrefillRef.current) return;
    const routeState = location.state as ProfRouteState | null;
    const prefill = routeState?.prefill;
    if (!prefill) return;
    const selectHandoffFigure = () => {
      const reference = parseProfFigureReference(routeState.figureReference);
      const imageRef = routeState.figurePreview?.imageRef ?? (reference?.kind === 'asset' ? reference.imageRef : null);
      if (reference && imageRef) void selectProfFigure(THREAD_ID, {
        reference, imageRef, label: routeState.figurePreview?.label ?? 'Figura selezionata',
      });
    };
    if (routeState?.autoSend !== true) {
      consumedPrefillRef.current = true;
      window.history.replaceState({}, '', location.pathname);
      return;
    }
    if (!hasSeenAIDisclosure()) {
      const consumeDeferredPrefill = () => {
        if (consumedPrefillRef.current) return;
        if (!hasSeenAIDisclosure()) return;
        consumedPrefillRef.current = true;
        useChat.getState().clearThread(THREAD_ID);
        selectHandoffFigure();
        setLastSuggestions(null);
        setProfFeedbackByMessageId({});
        setVoiceVisualSet(null);
        lastRenderStatusRef.current = undefined;
        window.history.replaceState({}, '', location.pathname);
        void handleSend(prefill, routeState?.topicHint);
      };
      window.addEventListener(AI_DISCLOSURE_ACK_EVENT, consumeDeferredPrefill);
      return () => {
        window.removeEventListener(AI_DISCLOSURE_ACK_EVENT, consumeDeferredPrefill);
      };
    }
    consumedPrefillRef.current = true;
    useChat.getState().clearThread(THREAD_ID);
    selectHandoffFigure();
    setLastSuggestions(null);
    setProfFeedbackByMessageId({});
    setVoiceVisualSet(null);
    lastRenderStatusRef.current = undefined;
    // Swallow the router state so a back/refresh doesn't re-trigger.
    window.history.replaceState({}, '', location.pathname);
    // Fire-and-forget — handleSend is defined below so we ref it via closure
    // ordering: React calls the effect after render, handleSend is bound by then.
    void handleSend(prefill, routeState?.topicHint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state]);

  // V29 S2: brand ConfirmModal instead of the native window.confirm (the one
  // remaining browser-chrome dialog on a student surface).
  function handleNewConversation() {
    setConfirmNewConversation(true);
  }

  function confirmNewConversationNow() {
    setConfirmNewConversation(false);
    useChat.getState().clearThread(THREAD_ID);
    setLastSuggestions(null);
    setProfFeedbackByMessageId({});
    spokenMessageIdRef.current = null;
    setVoiceVisualSet(null);
    lastRenderStatusRef.current = undefined;
    cancelQwenUiTurn();
    qwenPersistenceAttemptedRef.current.clear();
    qwenVoice.stop();
  }

  // Mic icon → it-IT Web-Speech DICTATION into the composer text field, owned by
  // the Composer itself (no onMic prop passed). The voice-CALL lives on the
  // composer call icon (onCall). Both entries share the same 4-affordance
  // Composer everywhere (incl. the global sheet).
  const startQwenVoiceCall = useCallback(() => {
    qwenStartRequestedRef.current = true;
    spokenMessageIdRef.current = latestCompletedProfText(messages)?.id ?? null;
    setVoicePersistStatus('idle');
    setCallSeconds(0);
    setVoiceMode(true);
    void qwenVoice.start();
  }, [messages, qwenVoice.start]);

  useEffect(() => {
    const becameSupported = qwenVoice.supported && !wasQwenSupportedRef.current;
    wasQwenSupportedRef.current = qwenVoice.supported;
    if (!becameSupported || !voiceMode || qwenVoice.active || qwenStartRequestedRef.current) return;
    startQwenVoiceCall();
  }, [qwenVoice.active, qwenVoice.supported, startQwenVoiceCall, voiceMode]);

  // Cross-surface call entry: GlobalChatSheet (on a non-/prof screen) routes to
  // /prof with `state.startCall=true`. Honor it once, then swallow the state so a
  // back/refresh doesn't re-trigger. Only starts when realtime voice is supported.
  useEffect(() => {
    if (consumedStartCallRef.current) return;
    const routeState = location.state as ProfRouteState | null;
    if (!routeState?.startCall) return;
    consumedStartCallRef.current = true;
    window.history.replaceState({}, '', location.pathname);
    if (qwenVoice.supported && !qwenVoice.active) {
      startQwenVoiceCall();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state, qwenVoice.supported]);

  // MM:SS call timer for the overlay — ticks while a Qwen call is active, resets
  // on each new call (set to 0 in startQwenVoiceCall too, for the pending phase).
  useEffect(() => {
    if (!qwenVoice.active) return undefined;
    const id = window.setInterval(() => setCallSeconds((s) => s + 1), 1000);
    return () => window.clearInterval(id);
  }, [qwenVoice.active]);

  useEffect(() => {
    if (!qwenVoice.supported) return;
    if (qwenVoice.active) {
      qwenStartRequestedRef.current = false;
      return;
    }
    if (!voiceMode || qwenVoice.status !== 'idle' || qwenStartRequestedRef.current) return;
    setVoiceMode(false);
  }, [qwenVoice.active, qwenVoice.status, qwenVoice.supported, voiceMode]);

  function handleVoiceModeToggle() {
    if (qwenVoice.supported) {
      if (voiceMode || qwenVoice.active) {
        qwenStartRequestedRef.current = false;
        qwenVoice.stop();
        cancelQwenUiTurn();
        setVoiceMode(false);
        return;
      }

      startQwenVoiceCall();
      return;
    }

    if (voiceMode) {
      stopSpeech();
      setVoiceMode(false);
      return;
    }

    spokenMessageIdRef.current = latestCompletedProfText(messages)?.id ?? null;
    setVoiceMode(true);
  }

  function handleQwenVoiceRetry() {
    if (!qwenVoice.supported) return;
    cancelQwenUiTurn();
    setVoicePersistStatus('idle');
    qwenStartRequestedRef.current = false;
    startQwenVoiceCall();
  }

  async function handleSend(
    text: string,
    topicHint?: ProfRouteState['topicHint'],
    opts?: { voiceNote?: boolean },
  ) {
    if (useChat.getState().threads[THREAD_ID]?.streaming || preparingFigureTurn.current) return;
    const figureToken = getToken();
    const figureSession = profFigureSessionId(THREAD_ID);
    preparingFigureTurn.current = true;
    let figureTurn: Awaited<ReturnType<typeof prepareProfFigureTurn>> = { ok: true };
    if (useChat.getState().pendingProfFiguresByThread[THREAD_ID]) {
      figureTurn = await prepareProfFigureTurn(THREAD_ID);
    }
    preparingFigureTurn.current = false;
    if (!figureTurn.ok || getToken() !== figureToken || profFigureSessionId(THREAD_ID) !== figureSession) return;
    // FP7 §1 (Mode B): a hold-to-talk voice note goes through the normal Prof v2
    // text path with voiceNote=true so the BE returns a CTRL `spoken` summary we
    // speak aloud. Distinct from the v1 voice responseMode below.
    const isVoiceNote = opts?.voiceNote === true;
    const requestVoiceResponseMode = voiceMode && (!qwenVoice.supported || qwenCallFailed);
    if (voiceMode && speaking) stopSpeech();
    if (qwenVoice.active || (voiceMode && qwenVoice.supported && !qwenCallFailed)) {
      qwenStartRequestedRef.current = false;
      qwenVoice.stop();
      cancelQwenUiTurn();
      setVoiceMode(false);
    }
    // Clear any post-refusal suggestions and retry-state — starting a new exchange.
    setLastSuggestions(null);
    setRetryQuestion(null);
    setVoicePersistStatus('idle');

    // 1. Collect history (last 10 messages) before appending the new one
    const currentMessages = useChat.getState().threads[THREAD_ID]?.messages ?? [];
    const historyMessages = currentMessages.slice(-10);
    const history: ChiediHistoryMessage[] = historyMessages
      .filter((m) => 'text' in m && (m.authorKind === 'you' || m.authorKind === 'prof'))
      .map((m) => ({
        role: m.authorKind === 'you' ? ('user' as const) : ('prof' as const),
        text: (m as { text: string }).text,
      }));

    // 2. Append user message
    useChat.getState().appendMessage(THREAD_ID, {
      id: crypto.randomUUID(),
      authorKind: 'you',
      kind: 'text',
      text,
    });

    // 3. Append streaming placeholder. Its UUID is also the server-side
    // assistant row id, so feedback always targets the exact rendered answer.
    const responseMessageId = crypto.randomUUID();
    useChat.getState().appendMessage(THREAD_ID, {
      id: responseMessageId,
      authorKind: 'prof',
      kind: 'streaming',
      text: '',
      feedbackEligible: false,
    });
    useChat.getState().setStreaming(THREAD_ID, true);

    // 4. Stream
    // W3 Phase B: pull the cached BE-issued chat-thread session id (if any)
    // and send it on this turn. On the very first message of a fresh thread
    // it's null — the BE will create one and emit it via the `session` SSE
    // event below, which we cache so subsequent turns reuse it.
    const cachedSessionId = useChat.getState().chiediSessionId;
    const cachedTopicState = cachedSessionId
      ? useChat.getState().chiediTopicStates[cachedSessionId]
      : undefined;
    try {
      await chiediStream(
        {
          question: text,
          ...(figureTurn.command ? { figureContext: figureTurn.command } : {}),
          responseId: responseMessageId,
          history,
          sessionId: cachedSessionId ?? undefined,
          ...(cachedTopicState ? { topicState: cachedTopicState } : {}),
          topicHint,
          ...(requestVoiceResponseMode ? { responseMode: 'voice' as const } : {}),
          ...(isVoiceNote ? { voiceNote: true as const } : {}),
        },
        {
          onFigureContext: (event) => {
            if (getToken() === figureToken) receiveProfFigureEvent(THREAD_ID, event);
          },
          onSession: (id) => {
            // Defensive: only take ownership of an id when we don't already
            // have one cached. Tab races where two browser tabs race-create
            // both succeed (each gets its own id) — no merge needed.
            const current = useChat.getState().chiediSessionId;
            if (!current) {
              useChat.getState().setChiediSessionId(id);
            }
          },
          onToken: (chunk) => {
            useChat.getState().appendToLastMessage(THREAD_ID, chunk);
          },
          onReplaceText: (fullText) => {
            useChat.getState().replaceLastMessageText(THREAD_ID, fullText);
          },
          onFollowUps: (questions) => {
            // Claude.ai-style continuation chips — always surface after a successful answer.
            // Append a standard "simplify" option so the student always has the
            // "spiegami in modo piu semplice" option alongside contextual follow-ups.
            const simplify = 'Puoi spiegarmelo in modo più semplice?';
            const dedup = questions.filter((q) => q !== simplify);
            setLastSuggestions([...dedup, simplify]);
          },
          // OA1 — server auto-issued encounter for this answer: render the V/F
          // card under the turn without any student tap.
          onCheckpoint: (encounter) => {
            const sessionIdForCard = useChat.getState().chiediSessionId;
            if (!sessionIdForCard) return;
            setCheckpointUiByMessageId((current) => ({
              ...current,
              [responseMessageId]: {
                kind: 'ready',
                encounter,
                idempotencyKey: crypto.randomUUID(),
                sessionId: sessionIdForCard,
              },
            }));
          },
          // OA1 — the server graded a typed/dictated answer exact-once: the
          // card renders already complete under the explanation turn.
          onCheckpointResult: (result, questionText) => {
            setCheckpointUiByMessageId((current) => ({
              ...current,
              [responseMessageId]: { kind: 'graded', result, questionText },
            }));
          },
          onDone: (done) => {
            useChat.getState().finalizeStreamingMessage(THREAD_ID);
            const activeSessionId = useChat.getState().chiediSessionId;
            if (activeSessionId && done?.topic_state) {
              useChat.getState().setChiediTopicState(activeSessionId, done.topic_state);
            }
            const persistenceOutcome = done?.assistant_persistence_outcome
              ?? (done?.response_persisted === true ? 'persisted' : undefined);
            if (done?.response_id === responseMessageId && persistenceOutcome) {
              useChat.getState().setMessageAssistantPersistenceOutcome(
                THREAD_ID,
                responseMessageId,
                persistenceOutcome,
              );
            }
            const durablyPersisted = persistenceOutcome === 'persisted'
              || persistenceOutcome === 'recovered_duplicate';
            if (durablyPersisted && done?.response_id === responseMessageId) {
              useChat.getState().setMessageFeedbackEligible(THREAD_ID, responseMessageId, true);
            }
            useChat.getState().setStreaming(THREAD_ID, false);
            // FP7 §1 (Mode B): for a voice note, speak the CTRL `spoken` summary
            // (≤300 chars) while the full text stays on screen; fall back to the
            // first paragraph of the latest answer when the model didn't fill it.
            if (isVoiceNote && speechSupported && (!qwenVoice.supported || qwenCallFailed)) {
              const summary = done?.spoken?.trim();
              const fallback = firstParagraph(latestCompletedProfText(
                useChat.getState().threads[THREAD_ID]?.messages ?? EMPTY_MESSAGES,
              )?.text ?? '');
              const toSpeak = summary || fallback;
              if (toSpeak) {
                spokenMessageIdRef.current = latestCompletedProfText(
                  useChat.getState().threads[THREAD_ID]?.messages ?? EMPTY_MESSAGES,
                )?.id ?? spokenMessageIdRef.current;
                speak(toSpeak, { lang: 'it-IT' });
              }
            }
          },
          onRefused: (_reason, message, suggestions) => {
            // Honor the BE contract for session_not_found: drop the cached
            // sessionId so the next student message creates a fresh thread.
            // Without this drop the FE would loop on the same stale id and
            // the student would be stuck repeating the refusal.
            if (_reason === 'session_not_found') {
              useChat.getState().setChiediSessionId(null);
            }
            useChat.getState().removeStreamingPlaceholder(THREAD_ID);
            useChat.getState().appendMessage(THREAD_ID, {
              id: crypto.randomUUID(),
              authorKind: 'prof',
              kind: 'text',
              text: message || 'Non ho una risposta sicura per questa. Prova a riformulare.',
            });
            useChat.getState().setStreaming(THREAD_ID, false);
            if (suggestions && suggestions.length > 0) {
              setLastSuggestions(suggestions);
            }
          },
          onError: (_err) => {
            if (getToken() === figureToken
                && useChat.getState().threads[THREAD_ID]?.messages.some((message) => message.id === responseMessageId)
                && receiveProfFigureError(THREAD_ID, _err)) {
              useChat.getState().removeStreamingPlaceholder(THREAD_ID);
              useChat.getState().setStreaming(THREAD_ID, false);
              setRetryQuestion(text);
              return;
            }
            useChat.getState().removeStreamingPlaceholder(THREAD_ID);
            useChat.getState().appendMessage(THREAD_ID, {
              id: crypto.randomUUID(),
              authorKind: 'prof',
              kind: 'text',
              text: 'Si è interrotta la connessione. Riprova tra qualche secondo.',
            });
            useChat.getState().setStreaming(THREAD_ID, false);
            setRetryQuestion(text);
          },
        },
      );
    } catch {
      useChat.getState().removeStreamingPlaceholder(THREAD_ID);
      useChat.getState().appendMessage(THREAD_ID, {
        id: crypto.randomUUID(),
        authorKind: 'prof',
        kind: 'text',
        text: 'Connessione interrotta. Riprova tra qualche secondo.',
      });
      useChat.getState().setStreaming(THREAD_ID, false);
      setRetryQuestion(text);
    }
  }

  function handleRetry() {
    if (!retryQuestion) return;
    const q = retryQuestion;
    setRetryQuestion(null);
    void handleSend(q);
  }

  // FP7 §1 (Mode B) — a hold-to-talk voice note. The Composer captures dictation
  // while the mic is held and hands the transcript here on release; we send it
  // through the normal Prof v2 text path flagged voiceNote=true, so the BE returns
  // a CTRL `spoken` ≤300-char summary that onDone speaks aloud (the spoken path is
  // already wired in handleSend). First use dismisses the one-line hint.
  function handleVoiceNote(text: string) {
    dismissVoiceNoteHint();
    void handleSend(text, undefined, { voiceNote: true });
  }

  const isEmpty = messages.length === 0;

  // V29 S7 (F15): load the latest resumable conversation for the inline
  // resume card — only while the thread is empty; silent absence on failure.
  useEffect(() => {
    if (!isEmpty) { setResumeCandidate(null); return; }
    let alive = true;
    void sessionsApi.recent(5)
      .then((p) => {
        if (!alive) return;
        setResumeCandidate(p.sessions.find((s) => s.mode === 'chiedi') ?? null);
      })
      .catch(() => { if (alive) setResumeCandidate(null); });
    return () => { alive = false; };
  }, [isEmpty]);
  const qwenPendingStart = voiceMode && qwenStartRequestedRef.current;
  const qwenCallUiOpen =
    qwenVoice.supported &&
    voiceMode &&
    (qwenVoice.active || qwenVoice.status !== 'idle' || qwenPendingStart);

  // ── Voice-CALL overlay state (fed to the presentational VoiceCallScreen) ──
  // The full-screen overlay is the REALTIME Qwen call surface only. The
  // browser-speech read-aloud fallback (no realtime engine) keeps the normal
  // chat + composer visible and just reads completed answers aloud; it gets a
  // small inline "Ferma voce" control instead of the full call surface.
  // Open whenever the call is engaged (toggled on via the composer, mid-pending,
  // or already active) so an active call always presents the overlay.
  // D2 — a failed call closes the overlay: the session is now in the
  // read-aloud fallback and the disclosure banner (below) says so.
  const voiceCallOverlayOpen = qwenVoice.supported && !qwenCallFailed && (qwenCallUiOpen || qwenVoice.active);
  // "active" drives the timer + rec dot + the live status copy. A pending-start
  // (call requested, not yet connected) is NOT active — the overlay shows the
  // "Tocca Chiama e parla" idle copy until the engine actually connects.
  const callActive = qwenVoice.active;
  // The hook exposes a separate `speaking` flag that can be true while status is
  // still 'idle'/'listening'; surface it as 'speaking' so the overlay shows the
  // interrupt control + "Il Prof sta rispondendo" while the answer plays.
  const callStatus: VoiceCallStatus = qwenVoice.speaking
    ? 'speaking'
    : (qwenVoice.status as VoiceCallStatus);
  // Fallback read-aloud is "on" whenever voiceMode is engaged without realtime
  // voice support — used to drive the inline stop-speech control + send mode.
  const fallbackVoiceMode = (!qwenVoice.supported || qwenCallFailed) && voiceMode;

  return (
    <Screen>
      <TopChrome
        title="Il Prof"
        subtitle="Tutor · sempre con te"
        leading={<ProfMark size={30} />}
        actions={
          <button
            type="button"
            onClick={() => setCronologiaOpen(true)}
            aria-label="Apri cronologia"
            data-testid="prof-cronologia-button"
            style={{
              width: 44, height: 44, borderRadius: 10, border: 'none',
              background: 'transparent', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: tokens.textMuted,
            }}
          >
            {/* Clock-with-arrow "history" icon. */}
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 3-6.7" />
              <path d="M3 4v5h5" />
              <path d="M12 8v4l3 2" />
            </svg>
          </button>
        }
        onNew={messages.length > 0 ? handleNewConversation : undefined}
      />
      <CronologiaPanel visible={cronologiaOpen} onClose={() => setCronologiaOpen(false)} />
      <ConfirmModal
        open={confirmNewConversation}
        title="Nuova conversazione?"
        body="La conversazione attuale resta nella cronologia; qui ripartiamo da zero."
        confirmLabel="Ricomincia"
        cancelLabel="Annulla"
        onConfirm={confirmNewConversationNow}
        onCancel={() => setConfirmNewConversation(false)}
      />

      {/* The normal Il Prof body (empty-state hero OR live thread) + chips +
          composer render only when the voice-call overlay is closed. When a call
          is active the overlay below is the single full-screen transcript, so the
          conversation is never rendered twice. */}
      {!voiceCallOverlayOpen && (isEmpty ? (
        /* Empty state: Prof greeting bubble + exactly 4 conversational chips.
           Every chip starts a conversation via handleSend; none navigates. */
        <div
          style={{
            flex:           1,
            overflowY:      'auto',
            padding:        '14px 16px 16px',
            display:        'flex',
            flexDirection:  'column',
            gap:            18,
          }}
        >
          {/* Prof greeting bubble (prof.html §26-35). First-name personalised. */}
          <ProfMessage>
            {(() => {
              const firstName = getStoredFirstName();
              const opener = firstName
                ? `Ciao ${firstName}, sono Il Prof. Sono qui per portarti all'esame con calma e senza sorprese.`
                : "Ciao, sono Il Prof. Sono qui per portarti all'esame con calma e senza sorprese.";
              return (
                <>
                  <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: tokens.text }}>
                    {opener}
                  </p>
                  <p style={{ margin: '10px 0 0', fontSize: 15, lineHeight: 1.55, color: tokens.text }}>
                    Possiamo ripartire da dove ci siamo lasciati, oppure dimmi tu da
                    cosa vuoi cominciare oggi.
                  </p>
                </>
              );
            })()}
          </ProfMessage>

          {/* V29 S7 (F15): the greeting PROMISES "ripartire da dove ci siamo
              lasciati" — this card finally delivers it inline. Latest resumable
              conversation; hidden when none exists or while it resumes. */}
          {resumeCandidate && (
            <button
              type="button"
              data-testid="prof-resume-card"
              disabled={resuming}
              aria-busy={resuming || undefined}
              onClick={() => {
                if (resuming) return;
                setResuming(true);
                void resumeProfSession(resumeCandidate.id)
                  .catch(() => {
                    useChat.getState().setChiediSessionId(null);
                    useChat.getState().clearThread('chiedi');
                  })
                  .finally(() => setResuming(false));
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                textAlign: 'left',
                background: tokens.surface,
                border: `1px solid ${tokens.lineStrong}`,
                borderRadius: tokens.radius.md,
                padding: '12px 14px',
                minHeight: 56,
                cursor: resuming ? 'wait' : 'pointer',
                boxShadow: '0 1px 2px rgba(26, 21, 53, 0.04)',
              }}
            >
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontFamily: tokens.fonts.mono, fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: tokens.accentInk }}>
                  Riprendi da dove eri
                </span>
                <span style={{ display: 'block', marginTop: 3, fontFamily: tokens.fonts.body, fontSize: 13.5, color: tokens.textMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {resuming ? 'Riprendo la conversazione…' : resumeCandidate.summary}
                </span>
              </span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={tokens.textFaint} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}

          {/* "Oppure inizia da qui" + EXACTLY 4 conversational chips. */}
          <div>
            <p style={{
              fontFamily:    tokens.fonts.mono,
              fontSize:      10,
              fontWeight:    600,
              color:         tokens.textFaint,
              letterSpacing: 0.6,
              textTransform: 'uppercase',
              margin:        '0 4px 10px',
            }}>
              Oppure inizia da qui
            </p>
            <ActionChips
              actions={START_CHIPS.map((chip) => ({
                label: chip.label,
                onClick: () => handleSend(chip.prompt),
              }))}
            />
          </div>
        </div>
      ) : (
        <ChatThread
          messages={messages}
          // B4 (V29 S2): citation chips were dead on /prof (no-op handlers)
          // while the identical bubbles expanded fine in the chat sheet and the
          // Simula review. Same store-backed wiring as ProfChatPanel now.
          expandedPrinciple={expandedPrinciple}
          onExpandPrinciple={expandPrinciple}
          onCollapsePrinciple={collapsePrinciple}
          resolvedPrinciple={null}
          feedbackByMessageId={profFeedbackByMessageId}
          onProfFeedback={handleProfFeedback}
          onRequestCheckpoint={handleRequestCheckpoint}
          checkpointByMessageId={checkpointByMessageId}
          checkpointIssueStateByMessageId={checkpointIssueStateByMessageId}
          {...(oa4ReviewEnabled && lastSuggestions && lastSuggestions.length > 0
            ? { followUps: lastSuggestions, onFollowUp: (q: string) => handleSend(q) }
            : {})}
        />
      ))}

      {/* Retry chip — shown after a network error so the student doesn't have
          to retype their last question. One-shot: cleared on click or next send. */}
      {!voiceCallOverlayOpen && retryQuestion && !isStreaming && (
        <div style={{ padding: '0 16px 8px', flexShrink: 0 }}>
          <ActionChips
            actions={[
              { kind: 'primary', label: 'Riprova ↻', onClick: handleRetry },
            ]}
          />
        </div>
      )}

      {/* Post-refusal / follow-up suggestions — keep the chat alive.
          DP1 (founder device pass 2026-07-13): ONE swipeable row + dismiss,
          not a vertical stack that permanently ate half the screen. */}
      {!oa4ReviewEnabled && !voiceCallOverlayOpen && lastSuggestions && lastSuggestions.length > 0 && !isStreaming && (
        <div
          data-testid="prof-suggestions-row"
          style={{ padding: '0 8px 8px 16px', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <ActionChips
              layout="row"
              actions={lastSuggestions.map((s) => ({
                label: s,
                onClick: () => handleSend(s),
              }))}
            />
          </div>
          <button
            type="button"
            data-testid="prof-suggestions-dismiss"
            aria-label="Nascondi i suggerimenti"
            onClick={() => useChat.getState().setLastSuggestions(null)}
            style={{
              flexShrink: 0,
              width: 30,
              height: 30,
              borderRadius: '50%',
              border: `1px solid ${tokens.line}`,
              background: tokens.surface,
              color: tokens.textMuted,
              fontSize: 15,
              lineHeight: 1,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* D2 — honest-state disclosure (founder verbatim: "a message on screen
          saying we are switching to the fallback so i know the system
          failed"). Shown when a realtime call FAILED and the session switched
          to read-aloud; dismissible; offers a retry. */}
      {!voiceCallOverlayOpen && qwenCallFailed && !voiceFallbackNoticeDismissed && (
        <div
          role="alert"
          style={{
            flexShrink: 0,
            margin: '0 16px 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            border: `1px solid ${tokens.lineStrong}`,
            borderRadius: 10,
            background: tokens.surface2,
            padding: '8px 12px',
          }}
        >
          <span style={{ color: tokens.text, fontSize: 12, lineHeight: 1.35 }}>
            La chiamata vocale non è disponibile in questo momento: sto passando
            alla modalità di riserva e ti leggo le risposte ad alta voce. Puoi
            continuare a scrivere o dettare qui sotto.
          </span>
          <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => {
                setQwenCallFailed(false);
                setVoiceFallbackNoticeDismissed(true);
                startQwenVoiceCall();
              }}
              style={{
                border: `1px solid ${tokens.lineStrong}`,
                borderRadius: 8,
                background: tokens.surface,
                color: tokens.text,
                fontSize: 12,
                fontWeight: 700,
                padding: '6px 10px',
                cursor: 'pointer',
              }}
            >
              Riprova la chiamata
            </button>
            <button
              type="button"
              aria-label="Chiudi avviso"
              onClick={() => setVoiceFallbackNoticeDismissed(true)}
              style={{
                border: 'none',
                background: 'transparent',
                color: tokens.textMuted,
                fontSize: 14,
                cursor: 'pointer',
                padding: '4px 6px',
              }}
            >
              ✕
            </button>
          </span>
        </div>
      )}

      {/* Browser-speech read-aloud fallback (no realtime engine): a compact
          inline status + "Ferma voce" control. The composer call icon toggles
          this mode on; this row lets the student see it's on + stop the speech
          without leaving the chat (the full call overlay is Qwen-only). */}
      {!voiceCallOverlayOpen && fallbackVoiceMode && (
        <div
          role="status"
          aria-live="polite"
          style={{
            flexShrink: 0,
            margin: '0 16px 8px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            border: `1px solid ${tokens.lineStrong}`,
            borderRadius: 10,
            background: tokens.surface2,
            padding: '8px 12px',
          }}
        >
          <span style={{ color: tokens.textMuted, fontSize: 12, lineHeight: 1.25 }}>
            {speaking ? 'Il Prof sta rispondendo' : 'Pronta'}
          </span>
          {speaking && (
            <button
              type="button"
              aria-label="Ferma voce"
              onClick={stopSpeech}
              style={{
                flexShrink: 0,
                border: `1px solid ${tokens.lineStrong}`,
                borderRadius: 10,
                background: tokens.surface,
                color: tokens.text,
                minHeight: 36,
                padding: '6px 12px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Ferma voce
            </button>
          )}
        </div>
      )}

      {/* First-use hint for the layered hold-to-talk affordance (founder decision
          2026-06-11). Unobtrusive one-liner in Il Prof's Italian voice, shown once
          (localStorage) above the composer when the call overlay is closed. */}
      {!voiceCallOverlayOpen && showVoiceNoteHint && (
        <div
          role="status"
          style={{
            flexShrink: 0,
            margin: '0 16px 6px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            border: `1px solid ${tokens.line}`,
            borderRadius: 10,
            background: tokens.surface2,
            padding: '7px 12px',
          }}
        >
          <span style={{ color: tokens.textMuted, fontSize: 12, lineHeight: 1.3 }}>
            Tieni premuto il microfono per inviare una nota vocale.
          </span>
          <button
            type="button"
            aria-label="Ho capito"
            onClick={dismissVoiceNoteHint}
            style={{
              flexShrink: 0,
              border: 'none',
              background: 'transparent',
              color: tokens.textFaint,
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: 1,
              padding: 0,
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* 4-affordance composer (field + mic + call + gradient send). The mic
          DICTATES on TAP (it-IT Web-Speech, Composer-owned) and records a VOICE
          NOTE on press-AND-HOLD (founder decision 2026-06-11: hold-to-talk layered
          with dictation). The call icon starts a voice-CALL (or toggles the
          browser-speech read-aloud fallback when realtime voice is unsupported).
          Hidden while the call overlay owns the screen. */}
      {!voiceCallOverlayOpen && (
        <ProfFigureContext threadId={THREAD_ID} />
      )}
      {!voiceCallOverlayOpen && (
        <Composer
          placeholder={fallbackVoiceMode ? 'Parla o scrivi al Prof…' : 'Scrivi al Prof…'}
          disabled={isStreaming || savingFigure}
          voiceMode={fallbackVoiceMode}
          onSend={handleSend}
          onVoiceNote={handleVoiceNote}
          voiceNoteReview={oa4ReviewEnabled}
          {...(qwenVoice.supported || speechSupported ? { onCall: handleVoiceModeToggle } : {})}
        />
      )}

      {/* Voice-CALL presentation — the full-screen VoiceCallScreen rendered as an
          OVERLAY, driven entirely by ProfScreen's existing qwen voice state. ALL
          the voice orchestration + guards stay in ProfScreen; this surface only
          paints the orb + transcript + Stop. Covers BOTH the realtime Qwen path
          and the browser-speech read-aloud fallback. */}
      {voiceCallOverlayOpen && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 70 }}>
          <VoiceCallScreen
            /* Voice-visuals rebuild P0a — the SINGLE current visual set, rendered
               in ONE dedicated slot and replaced on each new selection. */
            visualSet={activeVisualSet?.signs.map((s) => ({ id: s.id, alt: s.alt }))}
            /* The live shared thread IS the call transcript — text, inline signs,
               V/F cards, streaming render exactly as in the chat, only once. */
            transcript={
              messages.length > 0 ? (
                <ChatThread
                  messages={messages}
                  // B4 (V29 S2): same live citation wiring in the call
                  // transcript — one thread, one behavior.
                  expandedPrinciple={expandedPrinciple}
                  onExpandPrinciple={expandPrinciple}
                  onCollapsePrinciple={collapsePrinciple}
                  resolvedPrinciple={null}
                  feedbackByMessageId={profFeedbackByMessageId}
                  onProfFeedback={handleProfFeedback}
                  onRequestCheckpoint={handleRequestCheckpoint}
                  checkpointByMessageId={checkpointByMessageId}
                  checkpointIssueStateByMessageId={checkpointIssueStateByMessageId}
                />
              ) : undefined
            }
            status={callStatus}
            active={callActive}
            elapsedSeconds={callSeconds}
            reconnecting={qwenVoice.reconnecting}
            onStop={handleVoiceModeToggle}
            onInterrupt={qwenVoice.interrupt}
            onRetry={handleQwenVoiceRetry}
          />
        </div>
      )}
    </Screen>
  );
}
